// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { newDiscussionHref } from 'lib/community/discussions'
import { productSlugFromHref } from 'lib/community/writer-handoff'
import { newTabProps, usePrefersNewTab } from 'lib/community/use-writer-handoff'
import {
  ASK_OWNERS,
  MAKE_IT_COUNT,
  ownersDeepLink,
  SEE_IN_COMMUNITY,
  SEE_ON_PAGE,
} from 'lib/owner-notes/band-copy'
import type { OwnerTrack } from 'lib/owner-notes/analytics'
import type { OwnerClaimResponse } from 'lib/dealhunt/types'
import ClaimCta from './ClaimCta'
import cta from './ClaimCta.module.css'
import styles from './OwnerNoteBand.module.css'

/** "Ask this →": the discussions writer opened as a question on this product, title filled. */
export function askOwnersHref(
  productHref: string | null | undefined,
  seed: string,
  trailingSlash: boolean
): string | null {
  const slug = productSlugFromHref(productHref)
  return slug
    ? newDiscussionHref({ kind: 'question', product: slug, title: seed }, trailingSlash)
    : null
}

interface AcceptActionsProps {
  /** `communityThreadHref`; null → the older actions (product page first, or Done alone). */
  thread: string | null
  productHref: string | null | undefined
  onClose: () => void
}

/**
 * The accept result's actions: the review in the community first, the product page second (when
 * the band knows it), Done the quiet way out.
 */
export const AcceptActions: React.VFC<AcceptActionsProps> = ({ thread, productHref, onClose }) => {
  const newTab = usePrefersNewTab()
  return (
    <div className={styles.actions}>
      {thread && (
        <a className={styles.primary} href={thread} {...newTabProps(newTab)}>
          {SEE_IN_COMMUNITY}
        </a>
      )}
      {productHref && (
        // A new tab: the reader keeps their place on the category grid (Guillaume, 2026-09-11).
        <a
          className={thread ? styles.secondary : styles.primary}
          href={ownersDeepLink(productHref)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {SEE_ON_PAGE}
        </a>
      )}
      <button
        type="button"
        className={thread || productHref ? styles.ghost : styles.primary}
        onClick={onClose}
      >
        {'Done'}
      </button>
    </div>
  )
}

interface MakeItCountProps {
  /** `makeItCountItems` — DealHunt's figures only. */
  items: string[]
  trackOwner: OwnerTrack
  /** The session's masked email (the band's). Omitted: `ClaimCta` reads it from the session —
   *  the reward screen, which has no band state. */
  signedInEmail?: string | null
  retry: 'link' | 'sign_in'
  onClaimed: (result: OwnerClaimResponse) => void
}

/**
 * A signed-out author's accepted review (2026-10-07): what signing in adds, then the claim CTA —
 * sign in → claim → `onClaimed`, never a link on its own (#81). `claim_started` fires with
 * `location: 'publish'`, the value the Publish-time link already reports. Drawn by the band's
 * accept result and by the community writer's reward screen (`RewardScreen`).
 */
export const MakeItCount: React.VFC<MakeItCountProps> = ({
  items,
  trackOwner,
  signedInEmail,
  retry,
  onClaimed,
}) => (
  <ClaimCta
    trackOwner={trackOwner}
    location="publish"
    lead={MAKE_IT_COUNT.title}
    signedInEmail={signedInEmail}
    retry={retry}
    fine={MAKE_IT_COUNT.fine}
    onClaimed={onClaimed}
  >
    <p className={cta.question}>{MAKE_IT_COUNT.lead}</p>
    <ul className={cta.list}>
      {items.map(item => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </ClaimCta>
)

interface AskOwnersProps {
  /** `askOwnersSeed` — from the writer's answers, never their sentence. */
  seed: string
  /** `askOwnersHref`. */
  href: string
}

/** A signed-in author's accepted review: one seeded question for the other owners (the band's
 *  accept result and the reward screen). */
export const AskOwners: React.VFC<AskOwnersProps> = ({ seed, href }) => {
  const newTab = usePrefersNewTab()
  return (
    <section className={cta.root} aria-label={ASK_OWNERS.title}>
      <p className={cta.lead}>{ASK_OWNERS.title}</p>
      <p className={cta.question}>{`“${seed}”`}</p>
      <div className={cta.buttons}>
        <a className={cta.primary} href={href} {...newTabProps(newTab)}>
          {ASK_OWNERS.button}
        </a>
      </div>
      <p className={cta.fine}>{ASK_OWNERS.fine}</p>
    </section>
  )
}
